import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User, AuthTokens, LoginRequest, RegisterRequest, AnonymousUserRequest } from '../types';

interface AuthState {
  user: User | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isInitializing: boolean; // true while silentRefresh is in progress on startup
  loading: boolean;
  error: string | null;

  // Actions
  login: (credentials: LoginRequest) => Promise<void>;
  register: (userData: RegisterRequest) => Promise<void>;
  loginWithGoogle: (idToken: string) => Promise<{ needsOnboarding: boolean }>;
  loginWithGoogleCode: (code: string) => Promise<{ needsOnboarding: boolean }>;
  createAnonymousUser: (data?: AnonymousUserRequest) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  silentRefresh: () => Promise<void>;
  clearError: () => void;
  setUser: (user: User) => void;
  setTokens: (tokens: AuthTokens) => void;
  updateProfile: (data: Partial<User> | FormData) => Promise<void>;
  refreshUser: () => Promise<void>;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Singleton promise to prevent concurrent silentRefresh calls.
// React StrictMode double-invokes effects; with ROTATE_REFRESH_TOKENS=True
// two simultaneous requests would blacklist the token and log the user out.
let _silentRefreshPromise: Promise<void> | null = null;

// One-time migration: clear old localStorage that stored tokens
try {
  const old = localStorage.getItem('retrosive-auth-storage');
  if (old) {
    const parsed = JSON.parse(old);
    if (parsed?.state?.tokens?.refresh) {
      // Old format had refresh in localStorage – remove it
      delete parsed.state.tokens;
      localStorage.setItem('retrosive-auth-storage', JSON.stringify(parsed));
    }
  }
} catch {
  // Ignore migration errors
}

/**
 * Parse DRF error responses into a human-readable message.
 * Only surfaces field-level validation errors to the user (email, password duplicate etc.).
 * Generic server errors are swallowed here – callers show a generic message instead.
 */
function parseDRFError(errorData: Record<string, unknown>, fallback: string): string {
  if (typeof errorData.detail === 'string') return errorData.detail;
  if (errorData.non_field_errors) {
    return Array.isArray(errorData.non_field_errors)
      ? errorData.non_field_errors.join(' ')
      : String(errorData.non_field_errors);
  }
  if (errorData.password) {
    return Array.isArray(errorData.password)
      ? errorData.password.join(' ')
      : String(errorData.password);
  }
  if (errorData.email) {
    return Array.isArray(errorData.email)
      ? errorData.email.join(' ')
      : String(errorData.email);
  }
  if (typeof errorData === 'object') {
    const firstKey = Object.keys(errorData)[0];
    if (firstKey) {
      const value = errorData[firstKey];
      return Array.isArray(value)
        ? `${firstKey}: ${value.join(' ')}`
        : `${firstKey}: ${value}`;
    }
  }
  return fallback;
}

/**
 * Extract a user-friendly error message from a failed fetch Response.
 * Logs full details to the console; returns a generic string for the UI.
 */
async function extractResponseError(response: Response, context: string): Promise<string> {
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    try {
      const body = await response.json();
      console.error(`[auth] ${context} – HTTP ${response.status}`, body);
      // Surface validation errors (e.g. duplicate email) but not internal details
      if (response.status < 500) {
        return parseDRFError(body as Record<string, unknown>, genericAuthError(context));
      }
    } catch (e) {
      console.error(`[auth] ${context} – failed to parse JSON`, e);
    }
  } else {
    // HTML or unexpected content (e.g. 404 page, nginx error)
    const text = await response.text().catch(() => '');
    console.error(`[auth] ${context} – HTTP ${response.status} (non-JSON)`, text.slice(0, 300));
  }
  return genericAuthError(context);
}

function genericAuthError(context: string): string {
  const map: Record<string, string> = {
    login: 'Incorrect email or password.',
    register: 'Registration failed. Please try again.',
    google: 'Google sign-in failed. Please try again.',
    logout: 'Logout failed.',
    profile: 'Failed to load profile.',
  };
  for (const key of Object.keys(map)) {
    if (context.toLowerCase().includes(key)) return map[key];
  }
  return 'Something went wrong. Please try again.';
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      tokens: null,
      isAuthenticated: false,
      isInitializing: false,
      loading: false,
      error: null,

      login: async (credentials: LoginRequest) => {
        set({ loading: true, error: null });

        try {
          const response = await fetch(`${API_BASE_URL}/auth/token/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include', // receive HttpOnly refresh cookie
            body: JSON.stringify(credentials),
          });

          if (!response.ok) {
            const msg = await extractResponseError(response, 'login');
            throw new Error(msg);
          }

          const data = await response.json();

          // Get user profile
          const profileResponse = await fetch(`${API_BASE_URL}/auth/profile/`, {
            headers: { 'Authorization': `Bearer ${data.access}` },
          });

          if (!profileResponse.ok) {
            console.error('[auth] login – failed to fetch profile', profileResponse.status);
            throw new Error('Failed to load your profile. Please try again.');
          }

          const user = await profileResponse.json();

          set({
            user,
            tokens: { access: data.access },
            isAuthenticated: true,
            loading: false,
          });
        } catch (error) {
          console.error('[auth] login error', error);
          set({
            error: error instanceof Error ? error.message : genericAuthError('login'),
            loading: false,
          });
          throw error;
        }
      },

      register: async (userData: RegisterRequest) => {
        set({ loading: true, error: null });

        try {
          const response = await fetch(`${API_BASE_URL}/auth/register/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include', // receive HttpOnly refresh cookie
            body: JSON.stringify(userData),
          });

          if (!response.ok) {
            const msg = await extractResponseError(response, 'register');
            throw new Error(msg);
          }

          const data = await response.json();

          set({
            user: data.user,
            tokens: { access: data.tokens.access },
            isAuthenticated: true,
            loading: false,
          });
        } catch (error) {
          console.error('[auth] register error', error);
          set({
            error: error instanceof Error ? error.message : genericAuthError('register'),
            loading: false,
          });
          throw error;
        }
      },

      loginWithGoogle: async (idToken: string) => {
        set({ loading: true, error: null });

        try {
          const response = await fetch(`${API_BASE_URL}/auth/callback/google/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ id_token: idToken }),
          });

          if (!response.ok) {
            const msg = await extractResponseError(response, 'google');
            throw new Error(msg);
          }

          const data = await response.json();

          set({
            user: data.user,
            tokens: { access: data.tokens.access },
            isAuthenticated: true,
            loading: false,
          });

          return { needsOnboarding: Boolean(data.needs_onboarding) };
        } catch (error) {
          console.error('[auth] loginWithGoogle error', error);
          set({
            error: error instanceof Error ? error.message : genericAuthError('google'),
            loading: false,
          });
          throw error;
        }
      },

      loginWithGoogleCode: async (code: string) => {
        set({ loading: true, error: null });

        try {
          const response = await fetch(`${API_BASE_URL}/auth/callback/google/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ code }),
          });

          if (!response.ok) {
            const msg = await extractResponseError(response, 'google');
            throw new Error(msg);
          }

          const data = await response.json();

          set({
            user: data.user,
            tokens: { access: data.tokens.access },
            isAuthenticated: true,
            loading: false,
          });

          return { needsOnboarding: Boolean(data.needs_onboarding) };
        } catch (error) {
          console.error('[auth] loginWithGoogleCode error', error);
          set({
            error: error instanceof Error ? error.message : genericAuthError('google'),
            loading: false,
          });
          throw error;
        }
      },

      createAnonymousUser: async (data: AnonymousUserRequest = {}) => {
        set({ loading: true, error: null });

        try {
          const response = await fetch(`${API_BASE_URL}/auth/anonymous/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include', // receive HttpOnly refresh cookie
            body: JSON.stringify({
              session_id: data.session_id || `anon_${Date.now()}`,
              avatar_color: data.avatar_color || '#6366f1',
            }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || 'Failed to create anonymous user');
          }

          const responseData = await response.json();

          set({
            user: responseData.user,
            tokens: { access: responseData.tokens.access },
            isAuthenticated: true,
            loading: false,
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to create anonymous user',
            loading: false,
          });
          throw error;
        }
      },

      logout: async () => {
        const { tokens } = get();

        try {
          await fetch(`${API_BASE_URL}/auth/logout/`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(tokens?.access ? { 'Authorization': `Bearer ${tokens.access}` } : {}),
            },
            credentials: 'include', // send HttpOnly refresh cookie for blacklisting
          });
        } catch (error) {
          console.error('Logout request failed:', error);
        }

        set({
          user: null,
          tokens: null,
          isAuthenticated: false,
          error: null,
        });
      },

      refreshToken: async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/auth/token/refresh/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include', // sends HttpOnly refresh cookie
          });

          if (!response.ok) {
            throw new Error('Token refresh failed');
          }

          const data = await response.json();

          set({
            tokens: { access: data.access },
          });
        } catch (error) {
          // If refresh fails, logout user
          get().logout();
          throw error;
        }
      },

      silentRefresh: async () => {
        // Deduplicate: if a refresh is already in flight, reuse that promise.
        // This prevents React StrictMode's double-effect invocation from sending
        // two concurrent refresh requests (which would blacklist the token).
        if (_silentRefreshPromise) {
          return _silentRefreshPromise;
        }

        _silentRefreshPromise = (async () => {
          set({ isInitializing: true });
          try {
            const response = await fetch(`${API_BASE_URL}/auth/token/refresh/`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include', // sends HttpOnly refresh cookie
            });

            if (!response.ok) {
              // 401: cookie expired or invalid – user must log in again
              if (response.status === 401) {
                set({ user: null, tokens: null, isAuthenticated: false, isInitializing: false });
              } else {
                // Other server error (5xx etc.) – don't log out, just unblock the UI
                set({ isInitializing: false });
              }
              return;
            }

            const data = await response.json();
            set({ tokens: { access: data.access }, isInitializing: false });
          } catch {
            // Network error (backend unreachable, offline, etc.) – don't log the user out.
            // apiFetch will handle individual request failures with its own 401 retry logic.
            set({ isInitializing: false });
          } finally {
            _silentRefreshPromise = null;
          }
        })();

        return _silentRefreshPromise;
      },

      updateProfile: async (data: Partial<User> | FormData) => {
        const { tokens } = get();

        if (!tokens?.access) {
          throw new Error('No access token available');
        }

        set({ loading: true, error: null });

        try {
          const isFormData = data instanceof FormData;

          const headers: HeadersInit = {
            'Authorization': `Bearer ${tokens.access}`,
          };

          if (!isFormData) {
            headers['Content-Type'] = 'application/json';
          }

          const response = await fetch(`${API_BASE_URL}/auth/profile/`, {
            method: 'PATCH',
            headers,
            body: isFormData ? data : JSON.stringify(data),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || 'Profile update failed');
          }

          const updatedUser = await response.json();

          set({
            user: updatedUser,
            loading: false,
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Profile update failed',
            loading: false,
          });
          throw error;
        }
      },

      setUser: (user: User) => {
        set({ user });
      },

      setTokens: (tokens: AuthTokens) => {
        set({ tokens, isAuthenticated: true });
      },

      refreshUser: async () => {
        const { tokens, isAuthenticated } = get();

        if (!isAuthenticated || !tokens?.access) {
          return;
        }

        try {
          const response = await fetch(`${API_BASE_URL}/auth/profile/`, {
            headers: {
              'Authorization': `Bearer ${tokens.access}`,
            },
          });

          if (response.ok) {
            const user = await response.json();
            set({ user });
          } else if (response.status === 401) {
            // Token expired – try silent refresh before logging out
            try {
              await get().silentRefresh();
              const newTokens = get().tokens;
              if (newTokens?.access) {
                const retryResponse = await fetch(`${API_BASE_URL}/auth/profile/`, {
                  headers: { 'Authorization': `Bearer ${newTokens.access}` },
                });
                if (retryResponse.ok) {
                  const user = await retryResponse.json();
                  set({ user });
                  return;
                }
              }
            } catch {
              // Silent refresh failed
            }
            get().logout();
          }
        } catch (error) {
          console.error('Failed to refresh user:', error);
        }
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'retrosive-auth-storage',
      partialize: (state) => ({
        user: state.user,
        // Only persist user and isAuthenticated flag – NOT tokens
        // Access token is in-memory only; refresh token is in HttpOnly cookie
        isAuthenticated: state.isAuthenticated,
      }),
      // When hydration completes, if the user was authenticated,
      // immediately mark as initializing so protected routes stay blocked
      // until silentRefresh resolves (prevents flash of protected content + instant logout).
      onRehydrateStorage: () => (state) => {
        if (state?.isAuthenticated) {
          state.isInitializing = true;
        }
      },
    }
  )
);
